import { z } from 'zod';
import type { LaunchFormState } from './form-state';
import {
  buildGitRepoAttachment,
  CustomSandboxSizeSchema,
  GIT_REPO_ATTACHMENT_ID,
  GitRepoAttachmentConfigSchema,
  isBuiltinResourceSize,
  type CustomSandboxSize,
  type PluginAttachmentValue,
  type ResourceSize,
} from '@runloop/reflex-contract';
import { type AgentEnvVar, type PromptMode } from '@reflex/shared';

const CONFIG_STORAGE_KEY_PREFIX = 'launch-agent-form';
// Sentinel used when no organization is active (e.g. signed-out / pre-auth).
const NO_ORG_SENTINEL = '__none__';

function configKeyFor(orgId: string | null): string {
  return `${CONFIG_STORAGE_KEY_PREFIX}:${orgId ?? NO_ORG_SENTINEL}`;
}

/**
 * Config fields that should outlive a single browser tab (model, agent type,
 * persona, blueprint, env vars, etc.). Stored in localStorage so a returning
 * user keeps their preferences across tabs and sessions.
 *
 * Prompt text and inline chat attachments are persisted as server-side agent
 * drafts instead (see `useAgentDraftAutosave`).
 */
export interface PersistedFormConfig {
  agentType: string;
  selectedProviderId: string | null;
  model: string;
  modelEndpointId: string | null;
  manualBlueprintName: string | null;
  resourceSize: ResourceSize | null;
  customSize: CustomSandboxSize | null;
  dockerd: boolean;
  computerUse: boolean;
  idleTimeMinutes: number | null;
  attachmentValues: Record<string, PluginAttachmentValue>;
  envVars: AgentEnvVar[];
  selectedPersonaId: string | null;
  systemPrompt: string | null;
  selectedProviderSecretId: string | null;
  reasoningEffort: string | null;
  promptMode: PromptMode | null;
  /**
   * Labels and group carried over to the next launch, like every other chip
   * in the Advanced bar. Work arrives in runs of the same kind — three fixes
   * against the same migration — so remembering the filing is right more
   * often than clearing it, and the collapsed section's "customized" badge is
   * what keeps a stale choice from being invisible.
   */
  labelIds: string[];
  groupId: string | null;
}

/**
 * Drop the pinned branch from a remembered `git-repo` attachment, keeping the
 * repo itself.
 *
 * The repo is a durable preference — people launch against the same one for
 * weeks. The branch is not: it belongs to the task that was just launched.
 * Carrying it forward silently starts the next agent on a stale feature branch,
 * which is only noticed once the work is already on top of it. With no
 * `repoBranch` the launch resolves the repository's default branch at setup
 * time, which is what an unconfigured launch should mean.
 *
 * Applied on save (so the branch is never written) and on load (so entries
 * written before this behavior existed stop resurfacing their branch).
 */
function forgetRepoBranch(
  attachmentValues: Record<string, PluginAttachmentValue>,
): Record<string, PluginAttachmentValue> {
  const entry = attachmentValues[GIT_REPO_ATTACHMENT_ID];
  if (!entry) return attachmentValues;
  const parsed = GitRepoAttachmentConfigSchema.safeParse(entry.config);
  // A config that doesn't parse carries no branch we can trust to strip; leave
  // it byte-for-byte alone rather than rewriting something we can't read.
  if (!parsed.success || parsed.data.repoBranch === undefined) return attachmentValues;

  // Rebuild through the contract's builder so the branchless config matches
  // what the picker writes (notably: only a non-default `repoAccess` is kept).
  const branchless = buildGitRepoAttachment({
    repoSlug: parsed.data.repoSlug,
    repoAccess: parsed.data.repoAccess,
  });
  const next = { ...attachmentValues };
  if (branchless) {
    next[GIT_REPO_ATTACHMENT_ID] = { ...entry, config: branchless.config };
  } else {
    // No repo slug: a branch on its own is not something to remember.
    delete next[GIT_REPO_ATTACHMENT_ID];
  }
  return next;
}

/**
 * Save durable launch form config for the given organization (localStorage).
 */
export function saveFormState(state: LaunchFormState, orgId: string | null): void {
  saveConfig(state, orgId);
}

function saveConfig(state: LaunchFormState, orgId: string | null): void {
  try {
    const config: PersistedFormConfig = {
      agentType: state.agentType,
      selectedProviderId: state.selectedProviderId,
      model: state.model,
      modelEndpointId: state.modelEndpointId,
      manualBlueprintName: state.manualBlueprintName,
      resourceSize: state.resourceSize,
      customSize: state.customSize,
      dockerd: state.dockerd,
      computerUse: state.computerUse,
      idleTimeMinutes: state.idleTimeMinutes,
      attachmentValues: forgetRepoBranch(state.attachmentValues),
      envVars: state.envVars,
      selectedPersonaId: state.selectedPersonaId,
      systemPrompt: state.systemPrompt,
      selectedProviderSecretId: state.selectedProviderSecretId,
      reasoningEffort: state.reasoningEffort,
      promptMode: state.promptMode,
      labelIds: state.labelIds,
      groupId: state.groupId,
    };
    localStorage.setItem(configKeyFor(orgId), JSON.stringify(config));
  } catch (error) {
    console.warn('Failed to save launch agent form config:', error);
  }
}

/**
 * Load durable launch form config for the given organization.
 * `null` only when there is no saved config at all, so that callers can tell
 * apart "fresh load" vs "restored state".
 */
export async function loadFormState(orgId: string | null): Promise<PersistedFormConfig | null> {
  // Async signature kept for backward compatibility with callers awaiting the
  // result (legacy storage path used IndexedDB).
  return Promise.resolve(loadFormStateSync(orgId));
}

function loadFormStateSync(orgId: string | null): PersistedFormConfig | null {
  try {
    const configStored = readSafely(() => localStorage.getItem(configKeyFor(orgId)));
    if (!configStored) return null;

    const parsedConfig = JSON.parse(configStored) as Partial<PersistedFormConfig>;
    if (!parsedConfig.agentType || typeof parsedConfig.agentType !== 'string') {
      return null;
    }

    // Validate persisted resourceSize against the canonical enum so a stale
    // value (e.g. removed/renamed size from a future schema rollback) silently
    // falls back to "use blueprint default" instead of crashing the launcher.
    const parsedResourceSize = parsedConfig.resourceSize ?? null;
    const safeResourceSize =
      parsedResourceSize && isBuiltinResourceSize(parsedResourceSize) ? parsedResourceSize : null;

    // Same guard for a persisted custom size: a malformed or stale snapshot
    // falls back to the default rather than crashing the launcher. A custom
    // size the org has since removed still launches with its recorded
    // dimensions — matching how persisted launches behave server-side.
    const parsedCustomSize = parsedConfig.customSize ?? null;
    const safeCustomSize =
      parsedCustomSize && z.validate(CustomSandboxSizeSchema, parsedCustomSize)
        ? parsedCustomSize
        : null;

    const parsedIdle = parsedConfig.idleTimeMinutes;
    const safeIdleTimeMinutes =
      typeof parsedIdle === 'number' && Number.isInteger(parsedIdle) && parsedIdle > 0
        ? parsedIdle
        : null;

    return {
      agentType: parsedConfig.agentType,
      selectedProviderId: parsedConfig.selectedProviderId ?? null,
      model: parsedConfig.model || '',
      modelEndpointId: parsedConfig.modelEndpointId ?? null,
      manualBlueprintName: parsedConfig.manualBlueprintName ?? null,
      resourceSize: safeResourceSize,
      customSize: safeCustomSize,
      dockerd: parsedConfig.dockerd ?? false,
      computerUse: parsedConfig.computerUse ?? false,
      idleTimeMinutes: safeIdleTimeMinutes,
      attachmentValues: forgetRepoBranch(parsedConfig.attachmentValues ?? {}),
      envVars: parsedConfig.envVars ?? [],
      selectedPersonaId: parsedConfig.selectedPersonaId ?? null,
      systemPrompt: parsedConfig.systemPrompt ?? null,
      selectedProviderSecretId: parsedConfig.selectedProviderSecretId ?? null,
      reasoningEffort: parsedConfig.reasoningEffort ?? null,
      promptMode: (parsedConfig.promptMode as PromptMode) ?? null,
      // Ids only: a label or group deleted since the last launch simply stops
      // resolving, and the runner skips it rather than failing the launch.
      labelIds: Array.isArray(parsedConfig.labelIds) ? parsedConfig.labelIds : [],
      groupId: parsedConfig.groupId ?? null,
    };
  } catch (error) {
    console.warn('Failed to load launch agent form state:', error);
    return null;
  }
}

function readSafely<T>(fn: () => T): T | null {
  try {
    return fn();
  } catch {
    return null;
  }
}

/**
 * Clear persisted launch form config for the given organization.
 * Used by the "Clear" button to fully wipe saved preferences.
 */
export function clearPersistedFormState(orgId: string | null): void {
  try {
    localStorage.removeItem(configKeyFor(orgId));
  } catch (error) {
    console.warn('Failed to clear launch agent form config:', error);
  }
}

/**
 * Check if there is any persisted form config for the given organization.
 */
export async function hasPersistedFormState(orgId: string | null): Promise<boolean> {
  try {
    return readSafely(() => localStorage.getItem(configKeyFor(orgId))) !== null;
  } catch {
    return false;
  }
}
